'use client';

/**
 * Root error boundary — catches unhandled errors in any route segment that
 * doesn't have its own error.tsx. The root layout (sidebar, shell) remains
 * mounted; only the failing segment is replaced.
 *
 * For errors that crash the root layout itself, see global-error.tsx.
 */

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: ErrorProps) {
  const t = useTranslations('errorPage');

  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: 'root' } });
  }, [error]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-6 text-center px-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
        <AlertTriangle className="w-8 h-8 text-destructive" />
      </div>

      <div className="space-y-2">
        <h2 className="text-xl font-semibold">{t('title')}</h2>
        <p className="text-muted-foreground max-w-md">{t('rootDescription')}</p>
        {process.env.NODE_ENV === 'development' && (
          <pre className="mt-2 max-w-xl overflow-auto rounded bg-muted p-4 text-left text-xs text-destructive">
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        )}
      </div>

      <Button onClick={reset} variant="outline">
        {t('tryAgain')}
      </Button>
    </div>
  );
}
